import { useAuth } from '@/components/auth/authProvider';
import RequirePalmettoFinanceUser, { isPalmettoFinanceUser } from '@/components/auth/RequirePalmettoFinanceUser';
import { getOriginalUrl } from '@/helpers/documentHelpers';
import { trimFileNames } from '@/helpers/trimFileNames';
import { useDocumentUrl } from '@/hooks/useDocumentUrl';
import { useGetDocumentsQuery } from '@/services/documents';
import { downloadFile } from '@/services/helpers';
import { useGetQueueByAccountIdAndTypeQuery } from '@/services/queues';
import { useGetRequirementFlagSettingsQuery } from '@/services/requirementFlagSettings';
import { Box, Button, Icon, MediaModal, Spinner, toast, useBreakpoint } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ReactNode, SyntheticEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';

import {
  BillingDocumentTypes,
  DocumentDocument,
  DocumentLayout,
  documentTitles,
  Flag,
  FlagDocumentTypes,
  FlagStatus,
  MilestonePackageRoute,
  MilestoneRouteToQueueTypeMap,
  QueueStatus,
  RequirementFlagSettingsDocument,
  RequirementFlagSettingsType,
} from 'types';

import { useChecklistDrawerInset } from '../Checklists/checklistDrawerInset';
import { FlagActionsPopover } from '../QueueFlags/FlagActionsPopover';
import { QueueFlagsContext } from '../QueueFlags/QueueFlagsContext';

import { PdfViewer } from './PdfViewer';

export interface DocumentPreview {
  /** Unique identifier for this preview item (may include file index for flagging purposes) */
  id: string;
  /** The actual MongoDB document ID (used for API calls like refresh) */
  documentId?: string;
  /** URL for preview display (may be preview variant or original) */
  url: string;
  /** Original URL for downloads and full-size viewing (falls back to url if not provided) */
  originalUrl?: string;
  contentType: string;
  fileName: string;
  type?: string;
  hasError?: boolean;
  createdAt?: string;
  /** 1-based position of this file within its parent document's multi-file set, used to pick the right bounding boxes for image documents */
  pageNumber?: number;
}

interface DocumentPreviewModalProps {
  targetDoc: DocumentPreview;
  setTargetDoc: (doc: DocumentPreview) => void;
  isFilePreviewModalOpen: boolean;
  documentList: DocumentPreview[];
  closeFilePreviewModal: () => void;
  title?: string;
  baseCategory?: string;
  flagType?: RequirementFlagSettingsType;
  documentLayout?: DocumentLayout;
  enableCustomPdfViewer?: boolean;
}

const supportedPreviewContentTypes = new Set([
  'image/png',
  'image/jpeg',
  'application/pdf',
  'image/gif',
  'image/webp',
  'image/jfif',
  'image/svg+xml',
  'text/html',
]);
const EDITABLE_ELEMENT_SELECTOR = 'input, textarea, select, [contenteditable]:not([contenteditable="false"])';

interface PrevNextBtnWrapperProps {
  children: ReactNode;
  onClick: () => void;
  ariaLabel: string;
}

const PrevNextBtnWrapper = ({ children, onClick, ariaLabel }: PrevNextBtnWrapperProps) => (
  <Button
    onClick={onClick}
    aria-label={ariaLabel}
    tone="neutral"
    variant="primary"
    style={{ margin: '0 1em 0 1em', padding: '2em 1em', backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
  >
    {children}
  </Button>
);

export const DocumentPreviewModal = ({
  targetDoc,
  setTargetDoc,
  isFilePreviewModalOpen,
  documentList,
  closeFilePreviewModal,
  title,
  baseCategory,
  flagType,
  documentLayout,
  enableCustomPdfViewer: enableCustomPdfViewerProp,
}: DocumentPreviewModalProps) => {
  const checklistDrawerInset = useChecklistDrawerInset();
  const isChecklistDrawerOpen = checklistDrawerInset > 0;
  const { flagDrivenPackageReview, enableCustomPdfViewer: enableCustomPdfViewerFlag = false } = useFlags();
  const enableCustomPdfViewer = enableCustomPdfViewerProp ?? enableCustomPdfViewerFlag;
  const { refreshAndGetAllUrls } = useDocumentUrl();
  // Track which documents have already been attempted to prevent infinite refresh loops
  const attemptedRefreshesRef = useRef<Set<string>>(new Set());
  // Track when we're refreshing (API call + waiting for new image to load)
  const [isRefreshingImage, setIsRefreshingImage] = useState(false);
  const { data: requirementFlagSettings } = useGetRequirementFlagSettingsQuery(
    { type: flagType },
    { skip: !flagType },
  );
  const { isPhone } = useBreakpoint();
  const { id = '' } = useParams<{ id: string }>();
  const isSingleFile = (documentList?.length ?? 0) <= 1;
  const {
    getFlag,
    getFlagsByFlagType,
    shouldHide,
    resolveFlag,
    showResolveModal,
    showModal,
    isModalShowing,
    showEditModal,
    getVisibleFlags,
    flags,
  } = useContext(QueueFlagsContext);
  const navigate = useNavigate();
  const location = useLocation();
  const milestonePackageURI = location.pathname.split('/')[3];
  const dataKey = targetDoc?.id;
  const currentFlag = getFlag(targetDoc?.id, targetDoc?.fileName, false);
  const currentFlags = flagType ? getFlagsByFlagType(flagType, false, dataKey, targetDoc?.fileName) || [] : [];
  const attachedFlags = flags.filter(
    (f: Flag) => !f.resolved && f.attachedDocumentIds?.includes(targetDoc?.documentId ?? ''),
  );
  const allFlags = [...(currentFlags ? currentFlags : []), ...(currentFlag ? [currentFlag] : []), ...attachedFlags];
  const visibleFlags = getVisibleFlags(allFlags);
  const hasFlags = flagDrivenPackageReview ? Boolean(visibleFlags?.length) : Boolean(currentFlag);
  const flagsOnDisplay = hasFlags ? visibleFlags : [];
  const uploadedFlagDocuments = useGetDocumentsQuery({
    accountId: id,
    documentTypeFilter: [FlagDocumentTypes.flag],
  });
  const setting: RequirementFlagSettingsDocument | undefined = requirementFlagSettings?.requirementFlagSettings?.[0];

  const isSupportedPreviewContentType = supportedPreviewContentTypes.has(targetDoc?.contentType?.toLowerCase());
  const { claims } = useAuth();

  const isFinCoUser = isPalmettoFinanceUser(claims);

  const queueType = MilestoneRouteToQueueTypeMap[milestonePackageURI as MilestonePackageRoute];
  const { data: queue } = useGetQueueByAccountIdAndTypeQuery(
    {
      accountId: id,
      type: queueType,
    },
    { skip: !id || !queueType },
  );

  const onNext = useCallback(() => {
    let newTargetDocIndex = documentList.findIndex((doc) => doc.id === targetDoc?.id);
    if (newTargetDocIndex === documentList.length - 1 || newTargetDocIndex === -1) {
      newTargetDocIndex = 0;
    } else {
      newTargetDocIndex += 1;
    }
    setTargetDoc(documentList[newTargetDocIndex]);
  }, [documentList, setTargetDoc, targetDoc?.id]);

  const onPrev = useCallback(() => {
    let newTargetDocIndex = documentList.findIndex((doc) => doc.id === targetDoc?.id);
    if (newTargetDocIndex === 0) {
      newTargetDocIndex = documentList.length - 1;
    } else if (newTargetDocIndex === -1) {
      newTargetDocIndex = 0;
    } else {
      newTargetDocIndex -= 1;
    }
    setTargetDoc(documentList[newTargetDocIndex]);
  }, [documentList, setTargetDoc, targetDoc?.id]);

  const keyDownAndSwipeHandler = (event: any) => {
    // If we have the rename or tag modal open, we don't want to allow navigating of the images via the keyboard
    if (isModalShowing) {
      return;
    }
    if (event?.target instanceof Element && event.target.closest(EDITABLE_ELEMENT_SELECTOR)) {
      return;
    }

    if (event?.key === 'ArrowRight' || event?.dir === 'Left') {
      onNext();
    } else if (event?.key === 'ArrowLeft' || event?.dir === 'Right') {
      onPrev();
    }
  };

  /*
    Because we want to display previous and next buttons, we have to tweak the styling so that we effectively use up 100% of the width and height of the modal body
    Because of this, the built-in dismissal behavior of the modal, where clicking anywhere in the modal grey background, won't work
    This code mimics that functionality
    */
  const onModalBodyClick = useCallback(
    (event: SyntheticEvent) => {
      if (event.target instanceof HTMLDivElement) {
        closeFilePreviewModal();
      }
    },
    [closeFilePreviewModal],
  );

  useEffect(() => {
    document.addEventListener('keydown', keyDownAndSwipeHandler);
    return () => document.removeEventListener('keydown', keyDownAndSwipeHandler);
  });
  const swipeHandlers = useSwipeable({ onSwiped: keyDownAndSwipeHandler });

  const targetDocIdRef = useRef(targetDoc?.id);
  targetDocIdRef.current = targetDoc?.id;
  const targetDocRef = useRef(targetDoc);
  targetDocRef.current = targetDoc;
  const documentListRef = useRef(documentList);
  documentListRef.current = documentList;

  // Stable string signature — only changes when doc content actually changes.
  // documentList may get a new array reference every render (due to unstable context
  // function refs), so we compare by value rather than reference to avoid an infinite loop.
  const documentListSignature = useMemo(
    () => documentList?.map((d) => `${d.id}|${d.url}|${String(d.hasError)}`).join(',') ?? '',
    [documentList],
  );

  // Whenever a document is archived or unarchived, we want to refresh the actions possible, hence we need to refresh the targetDoc object
  useEffect(() => {
    const list = documentListRef.current;
    const newTargetDocIndex = list?.findIndex((doc) => doc.id === targetDocIdRef.current);
    if (newTargetDocIndex === -1) return;
    const freshDoc = list[newTargetDocIndex];
    const current = targetDocRef.current;
    if (
      freshDoc.url !== current?.url ||
      freshDoc.hasError !== current?.hasError ||
      freshDoc.fileName !== current?.fileName
    ) {
      setTargetDoc(freshDoc);
    }
  }, [documentListSignature, setTargetDoc]);

  const handleFlagClick = useCallback(async () => {
    if (hasFlags) {
      if (flagDrivenPackageReview) {
        showModal(dataKey, flagType, targetDoc?.fileName, baseCategory);
      } else {
        try {
          await resolveFlag(dataKey, FlagStatus.closed, targetDoc?.fileName);
        } catch (e) {
          toast.error('Something went wrong. Please try again.');
          console.error({ e });
        }
      }
    } else {
      showModal(dataKey, flagType, targetDoc?.fileName, baseCategory);
    }
  }, [hasFlags, resolveFlag, dataKey, targetDoc?.fileName, showModal, baseCategory]);

  const truncatedFileName = trimFileNames(targetDoc?.fileName);

  const handleAddNewFlag = () => {
    showModal(dataKey, flagType, targetDoc?.fileName, baseCategory, targetDoc?.type);
  };

  const handleImageLoadError = useCallback(async () => {
    // Use documentId for API calls (the actual MongoDB ID), fall back to id for backward compatibility
    const docId = targetDoc?.documentId ?? targetDoc?.id;
    if (!docId || !id) return;

    const refreshKey = `${id}-${docId}`;
    // Prevent infinite loops - only attempt refresh once per document
    if (attemptedRefreshesRef.current.has(refreshKey)) {
      // Mark as having an error so the UI can show an appropriate message,
      // and reset the spinner so the modal doesn't stay stuck in loading.
      setIsRefreshingImage(false);
      setTargetDoc({ ...targetDoc, hasError: true });
      return;
    }
    attemptedRefreshesRef.current.add(refreshKey);

    // Show loading state immediately
    setIsRefreshingImage(true);

    try {
      // Refresh all URLs in a single API call
      const refreshedUrls = await refreshAndGetAllUrls(id, docId);

      if (refreshedUrls?.previewUrl) {
        // Update URL - loading state stays true until onLoad fires
        setTargetDoc({
          ...targetDoc,
          url: refreshedUrls.previewUrl,
          originalUrl: refreshedUrls.originalUrl ?? targetDoc.originalUrl,
        });
      } else {
        // If refresh failed, mark as having an error
        setIsRefreshingImage(false);
        setTargetDoc({ ...targetDoc, hasError: true });
      }
    } catch (error) {
      console.error('Failed to refresh document URLs:', error);
      setIsRefreshingImage(false);
    }
  }, [targetDoc, id, refreshAndGetAllUrls, setTargetDoc]);

  const handleImageLoad = useCallback(() => setIsRefreshingImage(false), []);

  const handleDownload = useCallback(async () => {
    try {
      await downloadFile(targetDoc.originalUrl ?? targetDoc.url, targetDoc.fileName);
    } catch {
      toast.error('Failed to download file. Please try again later.');
    }
  }, [targetDoc]);

  const handleEditFlag = (flag: Flag) => {
    const flagIsEditable = Boolean(flag?.id && flag?.flagType);
    if (shouldHide) {
      if (flag.id) {
        handleRedirectToFlagDetails(id, flag.id?.toString());
      }
      return;
    }
    if (flagIsEditable) {
      showEditModal(flag);
    } else {
      showResolveModal(flag?.dataKey ?? '', targetDoc?.fileName, flag?.id?.toString());
    }
  };

  const handleRedirectToFlagDetails = (accountId: string, flagId: string) => {
    navigate(`/accounts/${accountId}/${milestonePackageURI}/flags/${flagId}`);
  };

  const showFlagToEPC = flagDrivenPackageReview
    ? !([QueueStatus.submitted, QueueStatus.resubmitted].includes(queue?.status) && !isFinCoUser)
    : true;
  const headerContent = useMemo(
    () =>
      targetDoc && (
        <Box direction="row" alignItems="center" flex="auto">
          <Box gap="sm" style={{ flex: 1, minHeight: '47px' }}>
            <Box fontWeight="bold">
              {title}
              {title && targetDoc?.type ? ' - ' : ''}
              {targetDoc?.type ? documentTitles[targetDoc.type as BillingDocumentTypes] : ''}
            </Box>
            <Box title={targetDoc.fileName}>{truncatedFileName}</Box>
          </Box>
          {!isPhone && (
            <Box direction="row" gap="xl" justifyContent="center" alignItems="center">
              {flagDrivenPackageReview ? (
                showFlagToEPC ? (
                  <FlagActionsPopover
                    flags={flagsOnDisplay}
                    dataKey={dataKey}
                    flagType={flagType}
                    fileName={targetDoc?.fileName}
                    baseCategory={baseCategory}
                    type={targetDoc?.type}
                    setting={setting}
                    onAddFlag={handleAddNewFlag}
                    onEditFlag={handleEditFlag}
                    shouldHide={shouldHide}
                  >
                    {hasFlags ? (
                      <Button style={{ color: 'white' }} tone="neutral" variant="tertiary" size="sm">
                        <Icon name="flag" size="md" />
                        <Icon name="caret-sm-down" size="md" />
                      </Button>
                    ) : !shouldHide ? (
                      <Button
                        style={{ color: 'white' }}
                        tone="neutral"
                        variant="tertiary"
                        size="sm"
                        iconPrefix={'flag-outline'}
                      />
                    ) : null}
                  </FlagActionsPopover>
                ) : null
              ) : (
                <RequirePalmettoFinanceUser>
                  <Button
                    style={{ color: 'white' }}
                    tone={hasFlags && !shouldHide ? 'danger' : 'neutral'}
                    variant={hasFlags && !shouldHide ? 'primary' : 'tertiary'}
                    size="sm"
                    iconPrefix="flag"
                    onClick={handleFlagClick}
                  ></Button>
                </RequirePalmettoFinanceUser>
              )}
              <Button
                aria-label="Download"
                tone="neutral"
                variant="tertiary"
                size="sm"
                iconPrefix="download"
                onClick={handleDownload}
                style={{ color: 'white' }}
              ></Button>
              <Button
                as="a"
                tone="neutral"
                variant="tertiary"
                size="sm"
                iconPrefix="launch-app"
                href={targetDoc.originalUrl ?? targetDoc.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'white' }}
              ></Button>
            </Box>
          )}
          {!isPhone && <Box style={{ flex: 1 }} />}
        </Box>
      ),
    [hasFlags, handleFlagClick, handleDownload, isPhone, shouldHide, targetDoc, title],
  );
  return (
    targetDoc && (
      <MediaModal
        isOpen={isFilePreviewModalOpen}
        onDismiss={closeFilePreviewModal}
        headerContent={headerContent}
        ariaLabel="File Preview Modal"
        dangerouslyBypassFocusLock={isChecklistDrawerOpen}
        {...swipeHandlers}
      >
        {hasFlags && !flagDrivenPackageReview
          ? flagsOnDisplay?.map((flag, i) => {
              const uploadedDocument: DocumentDocument = uploadedFlagDocuments?.data?.find(
                (d: DocumentDocument) => d.id === flag?.fileUpload,
              );
              return (
                <Box alignSelf="stretch" key={i}>
                  <Box
                    alignSelf="stretch"
                    alignItems="flex-start"
                    justifyContent="center"
                    direction="row"
                    padding="md"
                    background="secondary"
                    color="black"
                    gap="sm"
                    margin="md 0 0 0"
                  >
                    <Icon name="flag" size="md" color="danger" />
                    {flag.reason && <Box fontWeight="medium">{flag.reason}</Box>}
                    {flag?.problems?.length && (
                      <Box fontWeight="light">
                        {setting?.acceptanceCriteria?.find((criterion) => {
                          const criterionId = flag?.problems && flag?.problems[0]?.acceptanceCriteriaId.toString();
                          return criterion?.id?.toString() === criterionId;
                        })?.displayText || flag?.reason}
                      </Box>
                    )}
                    {(flag.reason || flag?.problems?.length) && flag.note && <Box fontWeight="medium">-</Box>}
                    {flag.note && <Box fontWeight="light">{flag.note}</Box>}

                    {flagDrivenPackageReview && (
                      <Button
                        onClick={() => showResolveModal(dataKey, undefined, flag?.id?.toString())}
                        size="xs"
                        variant="secondary"
                        tone="neutral"
                      >
                        Resolve
                      </Button>
                    )}
                  </Box>
                  {uploadedDocument && uploadedDocument?.files?.length > 0 && (
                    <Box
                      alignSelf="stretch"
                      alignItems="flex-start"
                      justifyContent="center"
                      direction="row"
                      padding="0 0 sm 0"
                      background="secondary"
                    >
                      <Box fontWeight="light" direction="row" gap="xs">
                        {uploadedDocument?.files?.map((file, index) => (
                          <Box key={index} direction="row" gap="xs" alignItems="center">
                            <Icon name="paperclip" color="body-secondary" aria-label="attachment" size="sm" />
                            <a
                              target="_blank"
                              rel="noreferrer noopener"
                              href={getOriginalUrl(file?.viewUrls)}
                              title={file.originalName}
                            >
                              {trimFileNames(file.originalName)}
                            </a>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            })
          : null}
        <Box
          direction="row"
          alignItems="center"
          style={{
            marginLeft: checklistDrawerInset,
            width: `calc(100vw - ${checklistDrawerInset}px)`,
            transition: 'margin-left 0.3s ease, width 0.3s ease',
            height: isPhone ? 'calc(100vh - 170px)' : 'calc(100vh - 105px)',
            marginTop: '40px',
          }}
          onClick={onModalBodyClick}
        >
          {!isSingleFile && (
            <PrevNextBtnWrapper ariaLabel="Previous file" onClick={onPrev}>
              <Icon name="caret-left" color="inverse-secondary" size="xl" />
            </PrevNextBtnWrapper>
          )}
          <Box style={{ flex: 1 }}>
            <Box display="flex" alignItems="center">
              {targetDoc.hasError && (
                <Box display="flex" background="secondary" padding="lg">
                  {targetDoc.fileName} could not be loaded.
                </Box>
              )}
              {!isSupportedPreviewContentType && (
                <Box background="secondary" padding="lg">
                  No preview available.
                </Box>
              )}
              {isRefreshingImage && (
                <Box
                  display="flex"
                  direction="column"
                  alignItems="center"
                  justifyContent="center"
                  gap="md"
                  padding="xl"
                >
                  <Spinner size="lg" />
                  <Box color="white">Refreshing document...</Box>
                </Box>
              )}
              {!targetDoc.hasError &&
                isSupportedPreviewContentType &&
                (targetDoc.contentType.startsWith('image/') && enableCustomPdfViewer ? (
                  <PdfViewer
                    key={targetDoc.id}
                    url={targetDoc.url}
                    fileName={targetDoc.fileName}
                    documentLayout={documentLayout}
                    contentType="image"
                    imagePageNumber={targetDoc.pageNumber}
                    onImageLoad={handleImageLoad}
                    onImageError={handleImageLoadError}
                    style={{
                      width: '100%',
                      height: isPhone ? 'calc(100vh - 185px)' : 'calc(100vh - 120px)',
                      // Hide the image while loading to prevent broken image flash
                      visibility: isRefreshingImage ? 'hidden' : 'visible',
                      position: isRefreshingImage ? 'absolute' : 'static',
                    }}
                  />
                ) : targetDoc.contentType.startsWith('image/') ? (
                  <img
                    key={targetDoc.id}
                    src={targetDoc.url}
                    alt={targetDoc.fileName}
                    onLoad={handleImageLoad}
                    onError={handleImageLoadError}
                    style={{
                      width: 'max-content',
                      height: 'max-content',
                      maxWidth: '100%',
                      maxHeight: isPhone ? 'calc(100vh - 185px)' : 'calc(100vh - 120px)',
                      objectFit: 'scale-down',
                      // Hide the image while loading to prevent broken image flash
                      visibility: isRefreshingImage ? 'hidden' : 'visible',
                      position: isRefreshingImage ? 'absolute' : 'static',
                    }}
                  />
                ) : targetDoc.contentType === 'application/pdf' && enableCustomPdfViewer ? (
                  <PdfViewer
                    key={targetDoc.id}
                    url={targetDoc.url}
                    fileName={targetDoc.fileName}
                    documentLayout={documentLayout}
                    style={{
                      width: '100%',
                      height: isPhone ? 'calc(100vh - 185px)' : 'calc(100vh - 120px)',
                    }}
                  />
                ) : (
                  <iframe
                    key={targetDoc.id}
                    src={targetDoc.url}
                    title={targetDoc.fileName}
                    style={{
                      width: '100%',
                      height: isPhone ? 'calc(100vh - 185px)' : 'calc(100vh - 120px)',
                    }}
                  />
                ))}
            </Box>
          </Box>
          {!isSingleFile && (
            <PrevNextBtnWrapper ariaLabel="Next file" onClick={onNext}>
              <Icon name="caret-right" color="inverse-secondary" size="xl" />
            </PrevNextBtnWrapper>
          )}
        </Box>
      </MediaModal>
    )
  );
};
